import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import reportCardApi, { errorMessage } from "./reportCardApi";

const TYPES = [
  { value: "SCHOLASTIC", label: "Scholastic" },
  { value: "CO_SCHOLASTIC", label: "Co-Scholastic" },
];
const BASES = [
  { value: "EXAM_TITLE", label: "Exam / Term column" },
  { value: "EXAM_GROUP", label: "Grand Total column" },
];

const PRESETS = {
  SCHOLASTIC: [
    ["A1", 90.01, 100], ["A2", 80.01, 90], ["B1", 70.01, 80], ["B2", 60.01, 70],
    ["C1", 50.01, 60], ["C2", 40.01, 50], ["D", 32.01, 40], ["E", 0, 32],
  ],
  CO_SCHOLASTIC: [["A", 80.01, 100], ["B", 60.01, 80], ["C", 40.01, 60], ["D", 32.01, 40], ["E", 0, 32]],
};

/**
 * Settings -> Grade Scales
 * One scale = (class or school default) + type + marking base. Values are percentages.
 */
const GradeScaleTab = ({ canEdit }) => {
  const [classes, setClasses] = useState([]);
  const [filter, setFilter] = useState({ class_id: "default", type: "SCHOLASTIC", grade_marking_base: "EXAM_TITLE" });
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({ grade: "", from: "", to: "" });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    reportCardApi.activeClasses().then(setClasses).catch(() => setClasses([]));
  }, []);

  const load = async () => {
    try {
      const list = await reportCardApi.grades.list(filter);
      setRows(list.map((g) => ({ ...g, from: Number(g.from), to: Number(g.to) })));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // gaps / coverage check shown to the user
  const coverage = useMemo(() => {
    if (!rows.length) return null;
    const sorted = [...rows].sort((a, b) => a.from - b.from);
    const issues = [];
    if (sorted[0].from > 0) issues.push(`nothing below ${sorted[0].from}%`);
    if (sorted[sorted.length - 1].to < 100) issues.push(`nothing above ${sorted[sorted.length - 1].to}%`);
    for (let i = 1; i < sorted.length; i++) {
      const gap = Math.round((sorted[i].from - sorted[i - 1].to) * 100) / 100;
      if (gap > 0.01) issues.push(`gap between ${sorted[i - 1].to}% and ${sorted[i].from}%`);
    }
    return issues;
  }, [rows]);

  const payload = (d) => ({
    grade: String(d.grade).trim().toUpperCase(),
    from: Number(d.from),
    to: Number(d.to),
    type: filter.type,
    grade_marking_base: filter.grade_marking_base,
    class_id: filter.class_id === "default" ? null : filter.class_id,
  });

  const validate = (d) => {
    if (!String(d.grade).trim()) return "Grade is required";
    if (d.from === "" || d.to === "") return "From and To are required";
    const f = Number(d.from), t = Number(d.to);
    if (isNaN(f) || isNaN(t) || f < 0 || t > 100) return "From / To must be between 0 and 100";
    if (f > t) return "From must be less than To";
    return null;
  };

  const save = async () => {
    const err = validate(draft);
    if (err) return toast.error(err);
    try {
      if (editId) await reportCardApi.grades.update(editId, payload(draft));
      else await reportCardApi.grades.create(payload(draft));
      toast.success("Saved");
      setDraft({ grade: "", from: "", to: "" });
      setEditId(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete grade ${row.grade}?`)) return;
    try {
      await reportCardApi.grades.remove(row.id);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const loadPreset = async () => {
    if (rows.length) return toast.error("This scale already has grades. Delete them first to load the preset.");
    try {
      for (const [grade, from, to] of PRESETS[filter.type]) {
        await reportCardApi.grades.create(payload({ grade, from, to }));
      }
      toast.success("Preset loaded");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
      load();
    }
  };

  return (
    <>
      <p className="text-muted">
        Grades use <b>percentage</b> (0–100), so the same scale works for a 100-mark subject, a 600-mark term and a 1200-mark grand total.
        A class without its own scale uses the <b>School Default</b>. The Grand Total column uses the Exam/Term scale unless you define a separate Grand Total scale.
      </p>

      <Row className="g-2 mb-3">
        <Col md={4}>
          <Form.Label className="form-view-label">Class</Form.Label>
          <Form.Select value={filter.class_id} onChange={(e) => setFilter({ ...filter, class_id: e.target.value })}>
            <option value="default">School Default (all classes)</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.class_name || c.classname}</option>)}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label className="form-view-label">Type</Form.Label>
          <Form.Select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })}>
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label className="form-view-label">Used for</Form.Label>
          <Form.Select value={filter.grade_marking_base} onChange={(e) => setFilter({ ...filter, grade_marking_base: e.target.value })}>
            {BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </Form.Select>
        </Col>
      </Row>

      {coverage && coverage.length > 0 && (
        <Alert variant="warning" className="py-2">Scale is incomplete: {coverage.join("; ")}.</Alert>
      )}

      <Table striped bordered size="sm">
        <thead>
          <tr><th>Grade</th><th>From %</th><th>To %</th>{canEdit && <th style={{ width: 110 }}>Action</th>}</tr>
        </thead>
        <tbody>
          {[...rows].sort((a, b) => b.from - a.from).map((r) => (
            <tr key={r.id} className={editId === r.id ? "table-info" : ""}>
              <td><b>{r.grade}</b></td><td>{r.from}</td><td>{r.to}</td>
              {canEdit && (
                <td>
                  <Button size="sm" className="edit-button me-2" onClick={() => { setEditId(r.id); setDraft({ grade: r.grade, from: r.from, to: r.to }); }}>
                    <i className="fa-regular fa-pen-to-square"></i>
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => remove(r)}><i className="fa fa-trash"></i></Button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="text-center text-muted">
              No grades in this scale.{" "}
              {canEdit && <Button size="sm" variant="link" onClick={loadPreset}>Load standard {filter.type === "SCHOLASTIC" ? "8-point (A1–E)" : "5-point (A–E)"} scale</Button>}
            </td></tr>
          )}
        </tbody>
        {canEdit && (
          <tfoot>
            <tr>
              <td><Form.Control size="sm" placeholder="A1" value={draft.grade} onChange={(e) => setDraft({ ...draft, grade: e.target.value })} /></td>
              <td><Form.Control size="sm" type="number" step="0.01" placeholder="90.01" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></td>
              <td><Form.Control size="sm" type="number" step="0.01" placeholder="100" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></td>
              <td>
                <Button size="sm" onClick={save}>{editId ? "Update" : "Add"}</Button>
                {editId && <Button size="sm" variant="link" onClick={() => { setEditId(null); setDraft({ grade: "", from: "", to: "" }); }}>Cancel</Button>}
              </td>
            </tr>
          </tfoot>
        )}
      </Table>
    </>
  );
};

export default GradeScaleTab;
